import { useState, useEffect, useRef } from "react";
import styled from "styled-components";

/* ---------- styled components ---------- */

const Wrapper = styled.div`
  width: min(420px, 80vw);
  max-width: 100%;
  font-family:
    system-ui,
    -apple-system,
    BlinkMacSystemFont,
    "SF Pro Text",
    "Segoe UI",
    sans-serif;
`;

const Panel = styled.div`
  border-radius: 22px;
  padding: 2px;
  background: radial-gradient(circle at top left, #ffe2c8, #ffffff);
  box-shadow: 0 24px 60px rgba(0, 0, 0, 0.12);
`;

const PanelInner = styled.div`
  border-radius: 20px;
  background: #fffdfc;
  padding: 20px 22px 22px;
  min-height: 280px;
  display: flex;
  flex-direction: column;
  gap: 18px;
`;

const HeaderRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 18px;
`;

const CandidateInfo = styled.div`
  flex: 1;
  min-width: 0;
`;

const CandidateName = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #231f1a;
  margin-bottom: 2px;
`;

const CandidateRole = styled.div`
  font-size: 13px;
  color: #7b746c;
`;

const ScoreWrapper = styled.div`
  width: 118px;
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScoreContainer = styled.div`
  position: relative;
  width: 96px;
  height: 96px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const ScoreSvg = styled.svg`
  width: 96px;
  height: 96px;
`;

const ScoreCircleBg = styled.circle`
  fill: none;
  stroke: #f0e7dd;
  stroke-width: 8;
`;

const ScoreCircleFg = styled.circle`
  fill: none;
  stroke: #2f9e44;
  stroke-width: 8;
  stroke-linecap: round;
  stroke-dasharray: 0 999;
  stroke-dashoffset: 0;
  transform: rotate(-90deg);
  transform-origin: 50% 50%;
`;

const ScoreCenter = styled.div`
  position: absolute;
  text-align: center;
  pointer-events: none;
`;

const ScoreValue = styled.div`
  font-size: 26px;
  font-weight: 700;
  color: #2f9e44;
  line-height: 1;
`;

const MatchSection = styled.div`
  border-top: 1px solid rgba(235, 94, 40, 0.16);
  padding-top: 14px;
`;

const MatchTitle = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: #231f1a;
  margin-bottom: 10px;
`;

const MatchPlaceholders = styled.div`
  display: flex;
  flex-direction: column;
  gap: 7px;
`;

const PlaceholderBar = styled.div`
  height: 8px;
  border-radius: 999px;
  background: linear-gradient(
    90deg,
    rgba(235, 94, 40, 0.26),
    rgba(235, 94, 40, 0.08)
  );
  opacity: 0.75;

  &:nth-child(1) {
    width: 70%;
  }

  &:nth-child(2) {
    width: 52%;
  }

  &:nth-child(3) {
    width: 62%;
  }
`;

/* ---------- component ---------- */

function ScoreFlow({ name = "John Doe", role = "Product Intern", score = 94 }) {
  const wrapperRef = useRef(null);
  const circleRef = useRef(null);
  const scoreRef = useRef(null);
  const frameRef = useRef(null);

  const [isVisible, setIsVisible] = useState(false);

  useEffect(() => {
    if (!isVisible) return;

    const circle = circleRef.current;
    const scoreEl = scoreRef.current;
    if (!circle || !scoreEl) return;

    const targetScore = Math.max(0, Math.min(score, 100)); // clamp 0–100
    const duration = 1200;

    const radius = circle.r.baseVal.value;
    const circumference = 2 * Math.PI * radius;

    circle.style.strokeDasharray = `${circumference} ${circumference}`;
    circle.style.strokeDashoffset = circumference;

    const start = performance.now();

    const animate = (now) => {
      const elapsed = now - start;
      const progress = Math.min(elapsed / duration, 1);

      const currentScore = Math.round(progress * targetScore);
      scoreEl.textContent = currentScore;

      const offset = circumference - (currentScore / 100) * circumference;
      circle.style.strokeDashoffset = offset;

      if (progress < 1) {
        frameRef.current = requestAnimationFrame(animate);
      }
    };

    frameRef.current = requestAnimationFrame(animate);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [score, isVisible]);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const observer = new IntersectionObserver(
      (entries) => {
        const entry = entries[0];
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      {
        threshold: 0.35,
      },
    );

    observer.observe(el);

    return () => observer.disconnect();
  }, []);

  return (
    <Wrapper ref={wrapperRef}>
      <Panel>
        <PanelInner>
          {/* Top row: name + role + score */}
          <HeaderRow>
            <CandidateInfo>
              <CandidateName>{name}</CandidateName>
              <CandidateRole>Applied for {role}</CandidateRole>
            </CandidateInfo>

            <ScoreWrapper>
              <ScoreContainer>
                <ScoreSvg viewBox="0 0 96 96" aria-hidden="true">
                  <ScoreCircleBg cx="48" cy="48" r="40" />
                  <ScoreCircleFg ref={circleRef} cx="48" cy="48" r="40" />
                </ScoreSvg>

                <ScoreCenter>
                  <ScoreValue ref={scoreRef}>0</ScoreValue>
                </ScoreCenter>
              </ScoreContainer>
            </ScoreWrapper>
          </HeaderRow>

          {/* Bottom row: why John’s a good match */}
          <MatchSection>
            <MatchTitle>Why John’s a good match</MatchTitle>
            <MatchPlaceholders>
              <PlaceholderBar />
              <PlaceholderBar />
              <PlaceholderBar />
            </MatchPlaceholders>
          </MatchSection>
        </PanelInner>
      </Panel>
    </Wrapper>
  );
}

export default ScoreFlow;
